/**
 * useManagedModels Hook
 *
 * Fetches the curated Microsoft-managed model list from the selected runtime,
 * which is the authoritative source used by runtime validation:
 *   `GET /runtime/webhooks/workflow/api/management/managedModels`
 *
 * If the runtime is unavailable or does not yet expose the endpoint, the hook
 * falls back to the control-plane managed-models endpoint. A successful empty
 * runtime response is authoritative and does not trigger the fallback.
 *
 * `isAvailable` is derived from `models.length > 0` after a successful fetch,
 * and stays `false` while loading or on error (so callers default-closed).
 *
 * Callers may pass `enabled: false` when discovery is not applicable to the
 * current surface. In that state no request is initiated.
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import {
  listApplicationManagedModels,
  listControlPlaneManagedModels,
  type ManagedModel,
} from '@/services/projectService';
import { useAppStore } from '@/stores/appStore';

// Temporary portal preference until the curated response carries its own default.
const PREFERRED_MANAGED_MODEL_IDS = 'gpt-5.6-luna,gpt-5';

export function selectDefaultManagedModel(
  models: ManagedModel[],
  preferredIds = PREFERRED_MANAGED_MODEL_IDS,
): string | undefined {
  const available = new Set(models.map((model) => model.id));
  return preferredIds.split(',').map((id) => id.trim()).find((id) => id && available.has(id));
}

// =============================================================================
// HOOK
// =============================================================================

export interface UseManagedModelsReturn {
  /** Curated model list. Empty while loading, on error, or when unavailable in region. */
  models: ManagedModel[];
  /** First configured preference present in the curated catalog, if any. */
  defaultModelId: string | undefined;
  /** True only after a successful fetch that returned at least one model. Callers should default-closed on this. */
  isAvailable: boolean;
  isLoading: boolean;
  error: Error | null;
  /** Fire-and-forget refetch. Deliberately returns `void` so callers can't create floating promises. */
  refetch: () => void;
}

export function useManagedModels(
  projectName: string | undefined,
  enabled = true,
): UseManagedModelsReturn {
  const { getOttoToken } = useAuth();
  const applicationName = useAppStore((state) => state.selectedAppName);

  const query = useQuery<ManagedModel[], Error>({
    queryKey: ['managed-models', projectName, applicationName],
    queryFn: async () => {
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to get Otto token');
      }

      if (applicationName) {
        try {
          return await listApplicationManagedModels(projectName as string, applicationName, token);
        } catch (error) {
          console.warn('Failed to list managed models from runtime; falling back to control plane.', error);
        }
      }

      // Temporary rollout fallback. Remove after the Otto runtime endpoint and
      // application-scoped BPM proxy are fully deployed across all regions.
      return listControlPlaneManagedModels(projectName as string, async () => token);
    },
    enabled: enabled && !!projectName,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const models = query.data ?? [];

  // `query.refetch` returns a promise; swallow it so the exposed signature is
  // honestly `() => void` and callers can't leave a floating promise behind.
  const { refetch: queryRefetch } = query;
  const refetch = useCallback(() => {
    void queryRefetch();
  }, [queryRefetch]);

  return {
    models,
    defaultModelId: selectDefaultManagedModel(models),
    // Default-closed: only true after a successful non-empty response.
    isAvailable: query.isSuccess && models.length > 0,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch,
  };
}
